import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useLoyaltyAccount from 'loyalty/hooks/useLoyaltyAccount'
import { rem } from 'polished'
import { useCallback, useContext } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import Divider from 'LuxKit/Divider'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import zIndex from 'styles/tools/z-index'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import ModalContext from 'contexts/ModalContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineEnvelopeAltIcon from 'LuxKit/Icons/line/LineEnvelopeAltIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import Tooltip from 'LuxKit/Tooltip'
import TextLink from 'LuxKit/TextLink'
import AssistChip from 'LuxKit/Chips/AssistChip'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Clickable from 'components/Common/Clickable'
import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'
import LoyaltyInfiniteSwash from 'loyalty/components/LoyaltyInfiniteSwash'
import LoyaltyInfiniteLogo from 'loyalty/components/LoyaltyInfiniteLogo'
import InfiniteConciergeDetailsModal from 'loyalty/components/InfiniteConciergeDetailsModal'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { LOYALTY_PURCHASE_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyPurchasePointsEnabled from 'loyalty/selectors/getIsLoyaltyPurchasePointsEnabled'
import useLoyaltyPurchasePointsOffers from 'loyalty/hooks/useLoyaltyPurchasePointsOffers'
import useInfiniteConciergeDetails from 'loyalty/hooks/useInfiniteConciergeDetails'

function getTimeBasedGreeting(intl: IntlShape): string {
  const hour = new Date().getHours()
  if (hour < 12) return intl.formatMessage({ id: 'ZbF0uWz63f6lExSbfzY5', defaultMessage: 'Good morning', description: 'Time-based greeting for morning hours on the promotional landing page' })
  if (hour < 18) return intl.formatMessage({ id: 'nHtQQILwxiUcDEVph28i', defaultMessage: 'Good afternoon', description: 'Time-based greeting for afternoon hours on the promotional landing page' })
  return intl.formatMessage({ id: '08Df2o1Sjgy5HQj1DK78', defaultMessage: 'Good evening', description: 'Time-based greeting for evening hours on the promotional landing page' })
}

const HeroSection = styled.div`
  position: relative;
  padding: ${rem(32)} ${rem(16)};
  overflow: visible;
  z-index: ${zIndex.loyaltyAccountHero};
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}

  @media (min-width: 450px) {
    padding: ${rem(40)} 0;
  }
`

/* Mobile-first: single centred column. Desktop: info column + points-card column. */
const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};
  text-align: center;

  ${mediaQueryUp.desktop} {
    display: grid;
    align-items: center;
    gap: ${rem(40)};
    grid-template-columns: 1fr ${rem(383)};
    text-align: start;
  }

  ${mediaQueryUp.largeDesktop} {
    gap: ${rem(40)};
  }
`

const InfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(12)};
  width: 100%;

  ${mediaQueryUp.desktop} {
    align-items: flex-start;
  }
`

const GreetingBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    align-items: flex-start;
    gap: ${rem(4)};
  }
`

const Lockup = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(7)};

  ${mediaQueryUp.desktop} {
    gap: ${rem(10)};
  }
`

const Swash = styled(LoyaltyInfiniteSwash)`
  height: ${rem(22)};
  width: auto;

  ${mediaQueryUp.desktop} {
    height: ${rem(32)};
  }
`

const InfiniteWordmark = styled(LoyaltyInfiniteLogo)`
  height: ${rem(13)};
  width: auto;

  ${mediaQueryUp.desktop} {
    height: ${rem(19)};
  }
`

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  line-height: 1;
`

/* Travel Concierge card — bordered, translucent over the dark Infinite hero. */
const ConciergeCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};
  width: 100%;
  padding: ${rem(12)};
  border: 1px solid rgba(212, 213, 216, 0.5);
  border-radius: ${props => props.theme.borderRadius.L};

  /* Match the points card's cap so the two stacked boxes line up below the two-column layout. */
  @media (min-width: 450px) {
    max-width: ${rem(400)};
  }

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(16)};
    padding: ${rem(12)} ${rem(20)};
    max-width: ${rem(400)};
  }
`

const ConciergeAvatar = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: ${rem(42)};
    height: ${rem(42)};
    border-radius: ${props => props.theme.borderRadius.round};
    background: ${props => props.theme.palette.product.loyalty.infinite.gradientTertiaryBackground};
    color: ${props => props.theme.palette.product.loyalty.infinite.gradientTertiaryContrast};
  }
`

const ConciergeContent = styled.div`
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    align-items: flex-start;
    gap: ${rem(4)};
  }
`

/* Concierge label + name — centred on mobile, left-aligned from desktop. */
const ConciergeHeadingBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(2)};

  ${mediaQueryUp.desktop} {
    align-items: flex-start;
  }
`

const RightColumn = styled.div`
  width: 100%;

  @media (min-width: 450px) {
    max-width: ${rem(400)};
  }

  ${mediaQueryUp.desktop} {
    max-width: none;
  }
`

/* Points balance card — light card, wrapped in the default theme so neutral surfaces don't flip. */
const PointsCard = styled.div`
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

const PointsCardHeader = styled.div`
  padding: ${rem(16)} ${rem(24)};
  text-align: center;
  background: ${props => props.theme.palette.product.loyalty.infinite.gradientSecondaryBackground};
  color: ${props => props.theme.palette.product.loyalty.infinite.normalContrast};
`

const PointsCardBody = styled.div`
  padding: ${rem(16)} ${rem(24)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.default.one};
`

function LoyaltyInfiniteAccountLandingPageHero() {
  const intl = useIntl()
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const account = useLoyaltyAccount().data!
  const [tier] = useCurrentLoyaltyTier()
  const [pendingPoints] = useCurrentLoyaltyPendingPoints()
  const isLoyaltyPurchasePointsEnabled = useAppSelector(getIsLoyaltyPurchasePointsEnabled)
  const { customerHasEligibleOffers } = useLoyaltyPurchasePointsOffers()
  const showModal = useContext(ModalContext)

  const handleConciergeInfoClick = useCallback(() => {
    showModal(<InfiniteConciergeDetailsModal />)
  }, [showModal])

  const conciergeDetails = useInfiniteConciergeDetails()
  const membershipNumber = `${account.accountNumber}`
  const showTopUp = isLoyaltyPurchasePointsEnabled && customerHasEligibleOffers

  const greetingLabel = firstName
    ? intl.formatMessage(
        {
          id: 'bPDeQctlPUJ9ihFL9O78',
          defaultMessage: '{greeting}, {firstName}',
          description: 'Greeting with member first name on loyalty account page',
        },
        { greeting: getTimeBasedGreeting(intl), firstName },
      )
    : getTimeBasedGreeting(intl)

  const callLabel = intl.formatMessage({
    id: 'S3x9a1lIvZKeFtBgPbKu',
    defaultMessage: 'Call',
    description: 'Chip label that dials the dedicated travel concierge phone number for Infinite members',
  })
  const emailLabel = intl.formatMessage({
    id: 'PsgS-0I3bTqiyNiit39v',
    defaultMessage: 'Email',
    description: 'Link label that opens the email client to contact the dedicated travel concierge',
  })

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <HeroSection className={`tier-${tier}`}>
      <LayoutContainer variant="medium">
        <HeroContent>
          <InfoGroup>
            <Lockup>
              <Swash />
              <InfiniteWordmark />
            </Lockup>

            <GreetingBlock>
              <Heading variant="heading3">{greetingLabel}</Heading>
              <Group direction="horizontal" gap={4} verticalAlign="center">
                <BodyText variant="medium">
                  {intl.formatMessage({ id: 'ksULOdf9OVP8fufigbjl', defaultMessage: 'Membership {membershipNumber}', description: 'Label showing user membership number on the promotional landing page' }, { membershipNumber })}
                </BodyText>
                <CopyToClipboardClickableIcon
                  value={membershipNumber}
                  ariaLabel={intl.formatMessage({ id: 'gxjYRARw_zM4ZUg47e33', defaultMessage: 'Copy membership number', description: 'Aria label for copy membership number button for the membership' })}
                />
              </Group>
            </GreetingBlock>

            {conciergeDetails && <ConciergeCard>
              <ConciergeAvatar>
                <LineUserIcon size={20} />
              </ConciergeAvatar>
              <ConciergeContent>
                <ConciergeHeadingBlock>
                  <Group direction="horizontal" gap={4} verticalAlign="center">
                    <BodyText variant="small" weight="bold" colour="tier-infinite-light">
                      {intl.formatMessage({
                        id: 'CpkauvVNMedU8Zm5nzWy',
                        defaultMessage: 'Your dedicated Travel Concierge',
                        description: 'Label above the assigned concierge name in the Infinite member account hero card',
                      })}
                    </BodyText>
                    <Clickable
                      onClick={handleConciergeInfoClick}
                      aria-label={intl.formatMessage({
                        id: 'D_AKmSbI2JytKsV37zEF',
                        defaultMessage: 'View travel concierge details',
                        description: 'Aria label for the Infinite concierge details info button',
                      })}
                    >
                      <InfoIconWrapper><LineInfoCircleIcon size={14} colour="tier-infinite-light" /></InfoIconWrapper>
                    </Clickable>
                  </Group>
                  <BodyText variant="medium">{conciergeDetails.name}</BodyText>
                </ConciergeHeadingBlock>
                <CSSBreakpoint max="tablet">
                  <Group direction="horizontal" gap={8} verticalAlign="center" className={getThemeClassName('inverse')}>
                    <AssistChip variant="outline" size="medium" startIcon={<LinePhoneIcon />} href={conciergeDetails.phoneHref}>
                      {callLabel}
                    </AssistChip>
                    <AssistChip variant="outline" size="medium" startIcon={<LineEnvelopeAltIcon />} href={conciergeDetails.emailHref}>
                      {emailLabel}
                    </AssistChip>
                  </Group>
                </CSSBreakpoint>
                <CSSBreakpoint min="desktop">
                  <Group direction="horizontal" gap={20} verticalAlign="center">
                    <Clickable href={conciergeDetails.phoneHref}>
                      <Group direction="horizontal" gap={4} verticalAlign="center">
                        <LinePhoneIcon size={16} />
                        <BodyText variant="medium">{conciergeDetails.phoneNumber}</BodyText>
                      </Group>
                    </Clickable>
                    <Clickable href={conciergeDetails.emailHref}>
                      <Group direction="horizontal" gap={4} verticalAlign="center">
                        <LineEnvelopeAltIcon size={16} />
                        <BodyText variant="medium">{emailLabel}</BodyText>
                      </Group>
                    </Clickable>
                  </Group>
                </CSSBreakpoint>
              </ConciergeContent>
            </ConciergeCard>}
          </InfoGroup>

          <RightColumn>
            <ProductPaletteProvider palette="default">
              <div className={getThemeClassName('default')}>
                <PointsCard>
                  <PointsCardHeader>
                    <BodyText variant="medium" weight="bold">
                      {intl.formatMessage({
                        id: '_2ZruFZvpCPNtPsmYCsA',
                        defaultMessage: 'Your points balance',
                        description: 'Heading on the points balance card in the Infinite member account hero',
                      })}
                    </BodyText>
                  </PointsCardHeader>
                  <PointsCardBody>
                    <Group direction="vertical" gap={12} horizontalAlign="center">
                      <Group direction="vertical" gap={4} horizontalAlign="center">
                        <Group direction="horizontal" gap={4} verticalAlign="baseline" horizontalAlign="center">
                          <Heading variant="heading2"><FormatDecimal value={account.points} /></Heading>
                          <BodyText variant="medium" weight="bold">
                            {intl.formatMessage({ id: 'DLJIxrZ4JoUzmTiyZSnk', defaultMessage: 'pts', description: 'Abbreviated points unit label displayed in the loyalty account menu tile' })}
                          </BodyText>
                        </Group>
                        <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="center">
                          <BodyText variant="medium">
                            <FormattedMessage
                              id="E5yjZp88aViZnM9Sfuqo"
                              defaultMessage="{count} pending"
                              description="Count of pending loyalty points that have not yet been credited to the account"
                              values={{ count: <FormatDecimal value={pendingPoints.points} /> }}
                            />
                          </BodyText>
                          <Tooltip description={intl.formatMessage({ id: 'MNjBpmX8tq42ZH7nJ7fk', defaultMessage: 'Pending points may take up to 24hrs to appear', description: 'Tooltip explaining pending points delay in the loyalty program section' })} placement="bottom">
                            <InfoIconWrapper><LineInfoCircleIcon size={16} /></InfoIconWrapper>
                          </Tooltip>
                        </Group>
                      </Group>
                      {showTopUp && <>
                        <Divider kind="secondary" />
                        <BodyText variant="medium">
                          <FormattedMessage
                            id="l14sRfIN8UqHHonGl4V_"
                            defaultMessage="Need more Société Points? <link>Top up now</link>"
                            description="Prompt on the Infinite points balance card linking to the top up points page"
                            values={{ link: chunks => <TextLink size="medium" weight="regular" underline to={`/${LOYALTY_PURCHASE_POINTS_PAGE}`}>{chunks}</TextLink> }}
                          />
                        </BodyText>
                      </>}
                    </Group>
                  </PointsCardBody>
                </PointsCard>
              </div>
            </ProductPaletteProvider>
          </RightColumn>
        </HeroContent>
      </LayoutContainer>
    </HeroSection>
  </ProductPaletteProvider>
}

export default LoyaltyInfiniteAccountLandingPageHero
